import React, { useEffect } from "react";
import Link from "next/link";
import Social from "@/components/layouts/Social";
import { Header } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData } from "@storyblok/react";

interface MobileProps {
  blok?: Header & SbBlokData;
}

/**
 * Helper to determine maximum depth for specific navigation sections.
 */
const getMaxLevelsForSection = (index: number): number => {
  if (index === 0) return 3; // First section allows 3 levels
  return 2; // Other sections allow 2 levels
};

const Mobile: React.FC<MobileProps> = ({ blok }) => {
  useEffect(() => {
    const handleDropdownClick = (e: Event) => {
      e.preventDefault();
      const targetId = (e.currentTarget as HTMLElement).getAttribute("href");
      if (targetId) {
        const slidePanel = document.querySelector(targetId);
        slidePanel?.classList.add("active");
      }
    };

    const handleBackButtonClick = (e: Event) => {
      e.preventDefault();
      const slidePanel = (e.currentTarget as HTMLElement).closest(".nav-mobile-slide");
      slidePanel?.classList.remove("active");
    };

    const dropdownTriggers = document.querySelectorAll(".nav-mobile-dropdown");
    dropdownTriggers.forEach((trigger) =>
      trigger.addEventListener("click", handleDropdownClick)
    );

    const backButtons = document.querySelectorAll(".navigation-back-btn");
    backButtons.forEach((button) =>
      button.addEventListener("click", handleBackButtonClick)
    );

    return () => {
      dropdownTriggers.forEach((trigger) =>
        trigger.removeEventListener("click", handleDropdownClick)
      );
      backButtons.forEach((button) =>
        button.removeEventListener("click", handleBackButtonClick)
      );
    };
  }, []);

  if (!blok) return null;

  /**
   * Recursive function to render navigation sections up to a specified depth level.
   */
  const renderNavItems = (
    sections: any[],
    maxLevel: number,
    currentLevel: number = 0
  ) => {
    return sections.slice(currentLevel === 1 ? 1 : 0).map((section) => {
      const {
        _uid,
        title,
        label,
        is_nested,
        link,
        items = [],
        children = [],
      } = section;

      // Use `title` for level 0 (top level), use `label` for deeper levels.
      const name = currentLevel === 0 ? title : label;

      if (!name) return null; // Skip rendering if no name is present

      const safeId = name.toLowerCase().replace(/\s+/g, "-");
      const childSections = items.length > 0 ? items : children;

      return (
        <li key={_uid} className={`nav-level-${currentLevel}`}>
          {is_nested && currentLevel + 1 < maxLevel ? (
            <>
              {/* Render expandable/collapsible section with toggle for 2nd+ levels */}
              {currentLevel === 1 && childSections.length > 0 ? (
                <>
                  <a
                    className="nav-mobile-slide-dropdown-link collapsed d-flex align-items-center justify-content-between"
                    data-bs-toggle="collapse"
                    href={`#nav-mobile-${safeId}`}
                    role="button"
                    aria-expanded="false"
                    aria-controls={`nav-mobile-${safeId}`}
                  >
                    {name}
                    <i className="fas fa-arrow-up fa-fw" />
                  </a>
                  <div
                    className="collapse multi-collapse"
                    id={`nav-mobile-${safeId}`}
                  >
                    <ul className="list-unstyled nav-mobile-submenu">
                      {renderNavItems(
                        childSections,
                        maxLevel,
                        currentLevel + 1
                      )}
                    </ul>
                  </div>
                </>
              ) : (
                // First Level Navigation (with dropdown functionality)
                <>
                  <a
                    href={`#nav-mobile-${safeId}`}
                    className="nav-mobile-dropdown d-flex align-content-center justify-content-between"
                    role="button"
                    aria-controls={`nav-mobile-${safeId}`}
                  >
                    {name}
                    <i className="fas fa-arrow-right fa-fw ms-2" />
                  </a>
                  <div className="nav-mobile-slide" id={`nav-mobile-${safeId}`}>
                    <div className="nav-mobile-slide-inner">
                      {/* Back navigation */}
                      <a href="#" className="navigation-back-btn text-white">
                        <i className="fas fa-arrow-left fa-fw" />
                      </a>
                      <div className="navbar-title h4 mb-0">{name}</div>
                      <ul className="list-unstyled nav-mobile-menu">
                        {renderNavItems(
                          childSections,
                          maxLevel,
                          currentLevel + 1
                        )}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            // Render a direct link if no further nesting is allowed
            <a
              href={link?.url || "#"}
              className="nav-mobile-link d-flex align-content-center justify-content-between"
              target={link?.target || "_self"}
              rel={
                link?.target === "_blank" ? "noopener noreferrer" : undefined
              }
            >
              {name}
            </a>
          )}
        </li>
      );
    });
  };

  return (
    <div
      className="offcanvas offcanvas-end d-lg-none nav-mobile"
      data-bs-backdrop="false"
      tabIndex={-1}
      id="page-navigation-mobile"
      aria-label="Mobile Navigation"
    >
      <div className="offcanvas-body p-0">
        <div className="d-flex flex-column justify-content-between h-100">
          <nav>
            <ul className="list-unstyled nav-mobile-menu">
              {(blok?.navigation_sections || []).map((section, index) =>
                renderNavItems([section], getMaxLevelsForSection(index))
              )}
            </ul>
          </nav>

          <div className="p-3 text-white text-center">
            <Social color="white" social_links={blok?.socials_mobile || []} />
            <Link
              href="/contact"
              className="mt-4 btn btn-primary rounded-pill d-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mobile;